import React, { useRef, useState, useEffect } from "react";
import {
  BiSolidSend,
  BiLogoLinkedin,
  BiSolidEnvelope,
  BiSolidCheckCircle,
  BiSolidError,
} from "react-icons/bi";
import { SiGithub } from "react-icons/si";
import { ToastContainer, toast, Zoom } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import AnimatedBackground from "../../common/AnimatedBackground";
import SEOHead from "../../common/SEOHead";
import Footer from "../Footer/Footer";

function SuccessToast() {
  return (
    <div className="flex items-center gap-3 px-1 py-0.5">
      <div className="w-9 h-9 rounded-xl bg-green-400/10 flex items-center justify-center text-green-300 shrink-0 border border-green-400/20">
        <BiSolidCheckCircle size={22} />
      </div>
      <div className="flex flex-col">
        <span className="text-white font-header font-bold text-[13px] tracking-tight">
          ¡Mensaje enviado!
        </span>
        <span className="text-gray-400 font-sans text-[11px] leading-tight mt-0.5">
          Te voy a responder lo antes posible.
        </span>
      </div>
    </div>
  );
}

function ErrorToast({ message }: { message?: string }) {
  return (
    <div className="flex items-center gap-3 px-1 py-0.5">
      <div className="w-9 h-9 rounded-xl bg-red-400/10 flex items-center justify-center text-red-300 shrink-0 border border-red-400/20">
        <BiSolidError size={22} />
      </div>
      <div className="flex flex-col">
        <span className="text-white font-header font-bold text-[13px] tracking-tight">
          Ups, hubo un error
        </span>
        <span className="text-gray-400 font-sans text-[11px] leading-tight mt-0.5">
          {message || "No se pudo enviar el mensaje, intentá de nuevo."}
        </span>
      </div>
    </div>
  );
}

export default function ContactPage() {
  const [name, setName] = useState("");
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submitButton = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    const PART_A = "d5520721-d67c-4070";
    const PART_B = "-8828-d91036891126";

    const fields = {
      k: "access_key",
      n: "name",
      e: "email",
      m: "message"
    };

    const formData = new FormData();
    formData.append(fields.k, PART_A + PART_B);
    formData.append(fields.n, name);
    formData.append(fields.e, email);
    formData.append(fields.m, message);

    try {
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        body: formData,
      });
      const result = await response.json();
      if (result.success) {
        toast(SuccessToast, {
          autoClose: 4000,
          className:
            "!bg-gray-900/90 !border !border-white/10 !backdrop-blur-xl !rounded-2xl !shadow-2xl !p-4",
          progressClassName: "!bg-green-300/40",
        });
        setName("");
        setEmail("");
        setMessage("");
      } else {
        toast(() => <ErrorToast message={result.message} />, {
          autoClose: 4000,
          className:
            "!bg-gray-900/90 !border !border-white/10 !backdrop-blur-xl !rounded-2xl !shadow-2xl !p-4",
          progressClassName: "!bg-red-400/40",
        });
      }
    } catch {
      toast(() => <ErrorToast />, {
        autoClose: 4000,
        className:
          "!bg-gray-900/90 !border !border-white/10 !backdrop-blur-xl !rounded-2xl !shadow-2xl !p-4",
        progressClassName: "!bg-red-400/40",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="relative w-full min-h-lvh text-white flex flex-col">
      <SEOHead
        title="Contacto"
        description="Contactate con Franco Martino, desarrollador de software especializado en React, TypeScript, Python y automatización. Abierto a oportunidades laborales, proyectos freelance y colaboraciones. Respuesta en menos de 24 horas."
        url="/contact"
      />
      <AnimatedBackground />
      <ToastContainer
        position="top-center"
        transition={Zoom}
        hideProgressBar
        closeButton={false}
      />

      <div className="relative flex-1 max-w-4xl mx-auto w-full px-6 sm:px-12 py-10">
        {/* Page header */}
        <div className="mb-10">
          <h1 className="text-3xl sm:text-4xl font-bold font-header text-white">
            Contacto
          </h1>
          <p className="text-gray-500 mt-2 text-sm max-w-xl">
            Si tenés un proyecto en mente, una oportunidad laboral o simplemente
            querés charlar sobre tecnología, estoy abierto a escuchar. Respondo
            todas las consultas en menos de 24 horas hábiles.
          </p>
        </div>

        <div className="h-px bg-gray-700/60 mb-10" />

        {/* Two-column layout */}
        <div className="flex flex-col lg:flex-row gap-12">
          {/* Left: info */}
          <div className="flex flex-col gap-6 lg:w-64 shrink-0">
            <div>
              <h2 className="text-white font-semibold font-header text-lg mb-1">
                Hablemos
              </h2>
              <p className="text-gray-500 text-sm leading-relaxed">
                Estoy abierto a oportunidades laborales, proyectos freelance y
                colaboraciones interesantes en desarrollo web, automatización,
                herramientas de productividad y proyectos de IoT.
              </p>
            </div>

            <div className="flex flex-col gap-3">
              <a
                href="mailto:martinofrancoezequiel@gmail.com"
                className="flex items-center gap-3 text-gray-400 hover:text-white transition-colors duration-200 group"
              >
                <span className="w-9 h-9 rounded-xl bg-gray-800/80 border border-gray-700/50 flex items-center justify-center group-hover:border-gray-600 transition-colors duration-200">
                  <BiSolidEnvelope size={17} />
                </span>
                <span className="text-sm">Correo</span>
              </a>
              <a
                href="https://www.linkedin.com/in/francomartino/"
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-3 text-gray-400 hover:text-white transition-colors duration-200 group"
              >
                <span className="w-9 h-9 rounded-xl bg-gray-800/80 border border-gray-700/50 flex items-center justify-center group-hover:border-gray-600 transition-colors duration-200">
                  <BiLogoLinkedin size={18} />
                </span>
                <span className="text-sm">LinkedIn</span>
              </a>
              <a
                href="https://github.com/FrancoMartino/"
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-3 text-gray-400 hover:text-white transition-colors duration-200 group"
              >
                <span className="w-9 h-9 rounded-xl bg-gray-800/80 border border-gray-700/50 flex items-center justify-center group-hover:border-gray-600 transition-colors duration-200">
                  <SiGithub size={16} />
                </span>
                <span className="text-sm">GitHub</span>
              </a>
            </div>

            {/* Response time badge */}
            <div className="flex items-center gap-2 bg-gray-800/60 rounded-xl px-4 py-3 border border-gray-700/40">
              <span className="relative flex size-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-300/60" />
                <span className="size-2 rounded-full bg-green-300" />
              </span>
              <span className="text-gray-400 text-xs">
                Respondo en menos de 24 hs
              </span>
            </div>
          </div>

          {/* Right: form */}
          <form onSubmit={handleSubmit} className="flex flex-col gap-4 flex-1">
            <div className="flex sm:flex-row flex-col gap-4">
              <div className="flex flex-col gap-1.5 flex-1">
                <label className="text-xs text-gray-500 font-medium tracking-wide uppercase">
                  Nombre
                </label>
                <input
                  className="w-full px-4 py-3 rounded-xl bg-gray-800/80 border border-gray-700/50 text-white placeholder-gray-600 focus:outline-none focus:border-green-300/50 focus:bg-gray-800 transition-all duration-200 text-sm"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Tu nombre"
                  required
                />
              </div>
              <div className="flex flex-col gap-1.5 flex-1">
                <label className="text-xs text-gray-500 font-medium tracking-wide uppercase">
                  Email
                </label>
                <input
                  className="w-full px-4 py-3 rounded-xl bg-gray-800/80 border border-gray-700/50 text-white placeholder-gray-600 focus:outline-none focus:border-green-300/50 focus:bg-gray-800 transition-all duration-200 text-sm"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="tu@email.com"
                  required
                />
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-xs text-gray-500 font-medium tracking-wide uppercase">
                Mensaje
              </label>
              <textarea
                className="w-full px-4 py-3 rounded-xl bg-gray-800/80 border border-gray-700/50 text-white placeholder-gray-600 focus:outline-none focus:border-green-300/50 focus:bg-gray-800 transition-all duration-200 text-sm min-h-40 resize-none"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Contame sobre tu proyecto o propuesta..."
                required
              />
            </div>

            <button
              ref={submitButton}
              disabled={isSubmitting}
              className="self-end flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium transition-all duration-200
                bg-green-300/10 border border-green-300/40 text-green-300
                hover:bg-green-300/20 hover:border-green-300/70
                disabled:bg-gray-800 disabled:border-gray-700/40 disabled:text-gray-500 disabled:cursor-not-allowed"
              type="submit"
            >
              <span>{isSubmitting ? "Enviando..." : "Enviar mensaje"}</span>
              <BiSolidSend
                size={15}
                className={isSubmitting ? "animate-spin" : ""}
              />
            </button>
          </form>
        </div>

      </div>

      <Footer />
    </div>
  );
}
